import { useState } from 'react';
import {
  getAllWinners,
  getWinnersByType,
  getWinnersByTimeFilter,
  getWinnersByWall,
  getWinnerById,
  getWinnerBadgeInfo,
  formatWinnerDate,
  getRelativeTime,
  getPositionLabel,
  winnerTypeLabels,
  winnerTypeIcons,
  winnerTypeColors,
  winnerTimeFilterLabels,
  winnerTimeFilterIcons,
  wallTypeLabels,
  wallTypeIcons,
  winnerBadgeConfigs,
  winnerGlobalStats,
  type RfwWinner,
  type WinnerType,
  type WinnerTimeFilter,
  type WallType,
  type WinnerBadge,
} from '@/mocks/rfwWinners';

/* ─── Winner Detail Modal ─── */
function WinnerDetailModal({ winner, onClose }: { winner: RfwWinner; onClose: () => void }) {
  const posInfo = getPositionLabel(winner.position);
  const typeColor = winnerTypeColors[winner.type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header image area */}
        <div className="relative h-44 bg-gradient-to-br from-amber-200 via-orange-100 to-yellow-200 flex items-center justify-center overflow-hidden rounded-t-2xl">
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          <img src={winner.photoUrl} alt={winner.name} className="w-28 h-28 rounded-full object-cover ring-4 ring-white shadow-xl relative z-10" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-20"
          >
            <i className="ri-close-line text-foreground-700"></i>
          </button>
          <div className="absolute top-4 left-4 z-10 text-2xl">{posInfo.emoji}</div>
        </div>

        <div className="p-5">
          {/* Name & Position */}
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-foreground-950">{winner.name}</h3>
            <div className="flex items-center justify-center gap-2 mt-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor}`}>
                <i className={`${winnerTypeIcons[winner.type]} mr-1 text-[10px]`}></i>
                {winnerTypeLabels[winner.type]}
              </span>
              <span className={`text-sm font-semibold ${posInfo.color}`}>{posInfo.label}</span>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-background-100">
              <p className="text-[10px] text-foreground-500 mb-0.5">Ciudad</p>
              <p className="text-sm font-medium text-foreground-900"><i className="ri-map-pin-line mr-1 text-foreground-400"></i>{winner.city}</p>
            </div>
            <div className="p-3 rounded-lg bg-background-100">
              <p className="text-[10px] text-foreground-500 mb-0.5">Radio</p>
              <p className="text-sm font-medium text-foreground-900"><i className="ri-radio-line mr-1 text-foreground-400"></i>{winner.radioName}</p>
            </div>
            <div className="p-3 rounded-lg bg-background-100">
              <p className="text-[10px] text-foreground-500 mb-0.5">Fecha</p>
              <p className="text-sm font-medium text-foreground-900"><i className="ri-calendar-line mr-1 text-foreground-400"></i>{formatWinnerDate(winner.date)}</p>
            </div>
            <div className="p-3 rounded-lg bg-background-100">
              <p className="text-[10px] text-foreground-500 mb-0.5">Puntos Ganados</p>
              <p className="text-sm font-bold text-amber-600">+{winner.scoreEarned} pts RFW</p>
            </div>
          </div>

          {/* Contest */}
          <div className="mb-4">
            <p className="text-xs text-foreground-500 mb-1">Competencia</p>
            <p className="text-sm font-medium text-foreground-900">{winner.contestName}</p>
          </div>

          {/* Prize */}
          <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-trophy-line text-amber-600"></i>
              <span className="text-sm font-bold text-amber-800">Premio</span>
            </div>
            <p className="text-sm font-semibold text-amber-900 mb-1">{winner.prize}</p>
            <p className="text-xs text-amber-700">{winner.prizeDescription}</p>
          </div>

          {/* Badges */}
          {winner.badges.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-foreground-500 mb-2">Insignias Obtenidas</p>
              <div className="flex flex-wrap gap-2">
                {winner.badges.map((badgeId) => {
                  const badge = getWinnerBadgeInfo(badgeId);
                  if (!badge) return null;
                  return (
                    <div key={badgeId} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${badge.bg} ${badge.border} border`}>
                      <span className="text-sm">{badge.emoji}</span>
                      <span className={`text-xs font-semibold ${badge.color}`}>{badge.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Score Bonus */}
          <div className="p-3 rounded-lg bg-background-100">
            <p className="text-[10px] text-foreground-500 mb-1">Bonus Total al Score RFW</p>
            <p className="text-lg font-bold text-amber-600">
              +{winner.scoreEarned + winner.badges.reduce((sum, b) => sum + (getWinnerBadgeInfo(b)?.scoreBonus || 0), 0)} pts
            </p>
            <p className="text-[10px] text-foreground-500 mt-0.5">{winner.scoreEarned} pts por ganar + {winner.badges.reduce((sum, b) => sum + (getWinnerBadgeInfo(b)?.scoreBonus || 0), 0)} pts por insignias</p>
          </div>

          {/* Program */}
          {winner.programName && (
            <div className="mt-4 p-3 rounded-lg bg-background-100">
              <p className="text-[10px] text-foreground-500 mb-0.5">Programa</p>
              <p className="text-sm font-medium text-foreground-900"><i className="ri-calendar-check-line mr-1 text-foreground-400"></i>{winner.programName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Winner Grid Card ─── */
function WinnerGridCard({ winner, onClick }: { winner: RfwWinner; onClick: () => void }) {
  const posInfo = getPositionLabel(winner.position);
  const typeColor = winnerTypeColors[winner.type];

  return (
    <div
      onClick={onClick}
      className="rounded-xl bg-background-50 border border-background-200/70 p-4 hover:border-amber-300/50 hover:shadow-sm transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="relative flex-shrink-0">
          <img src={winner.photoUrl} alt={winner.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-200" />
          <span className="absolute -bottom-1 -right-1 text-sm">{posInfo.emoji}</span>
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-foreground-950 text-sm truncate">{winner.name}</h4>
          <p className="text-xs text-foreground-500 mt-0.5">
            <i className="ri-radio-line mr-0.5 text-[10px]"></i>{winner.radioName}
          </p>
          <p className="text-[10px] text-foreground-400 mt-0.5">
            <i className="ri-map-pin-line mr-0.5"></i>{winner.city}
          </p>
        </div>
      </div>

      <p className="text-xs text-foreground-700 mb-2 line-clamp-2 font-medium">{winner.contestName}</p>

      <div className="flex items-center gap-1.5 mb-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeColor}`}>
          {winnerTypeLabels[winner.type]}
        </span>
        {winner.featured && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Destacado</span>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-background-200/70">
        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
          <i className="ri-trophy-line text-[10px]"></i>
          <span className="truncate max-w-[120px]">{winner.prize}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-foreground-500">
          <span>+{winner.scoreEarned} pts</span>
          <span>·</span>
          <span>{getRelativeTime(winner.date)}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Badge Card ─── */
function BadgeCard({ badge }: { badge: typeof winnerBadgeConfigs[number] }) {
  return (
    <div className={`rounded-xl p-4 border ${badge.border} ${badge.bg} transition-all duration-200 hover:shadow-sm`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{badge.emoji}</span>
        <div>
          <h4 className={`text-sm font-bold ${badge.color}`}>{badge.name}</h4>
          <p className="text-[10px] text-foreground-500">{badge.threshold}</p>
        </div>
      </div>
      <p className="text-xs text-foreground-600">{badge.description}</p>
      <div className="mt-2 flex items-center gap-1 text-[10px] text-amber-600 font-medium">
        <i className="ri-flashlight-line"></i>
        <span>+{badge.scoreBonus} pts bonus al Score</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════ */
export default function GanadoresPage() {
  const [activeType, setActiveType] = useState<WinnerType | 'todos'>('todos');
  const [activeTime, setActiveTime] = useState<WinnerTimeFilter>('semana');
  const [activeWall, setActiveWall] = useState<WallType>('landing');
  const [selectedWinner, setSelectedWinner] = useState<RfwWinner | null>(null);

  /* ─── Filter winners ─── */
  let filtered = getWinnersByType(activeType);
  filtered = filtered.filter((w) => {
    return getWinnersByTimeFilter(activeTime).some((fw) => fw.id === w.id);
  });

  const wallWinners = getWinnersByWall(activeWall).filter((w) => {
    return getWinnersByTimeFilter(activeTime).some((fw) => fw.id === w.id);
  });

  /* ─── Type tabs ─── */
  const typeTabs: { id: WinnerType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-trophy-line' },
    ...Object.entries(winnerTypeLabels).map(([id, label]) => ({
      id: id as WinnerType,
      label,
      icon: winnerTypeIcons[id as WinnerType],
    })),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100"></div>
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20celebration%20confetti%20pattern%20with%20golden%20sparkles%20and%20warm%20bokeh%20lights%2C%20minimalist%20elegant%20award%20ceremony%20background%20texture%2C%20soft%20champagne%20gold%20tones%20with%20subtle%20particle%20effects%2C%20premium%20festive%20atmosphere%2C%20clean%20modern%20celebratory%20design%2C%20high%20resolution%20seamless%20pattern&width=1600&height=600&seq=ganadores-hero-bg&orientation=landscape')] opacity-20 bg-cover bg-center"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-amber-700 text-xs font-semibold mb-4 border border-amber-200">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            SISTEMA DE GANADORES Y PREMIOS RFW
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
            Cada Participación Merece
            <span className="text-amber-600"> Reconocimiento</span>
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base mb-8">
            No solo premiamos con cosas físicas. El reconocimiento social, las insignias y el prestigio
            son el verdadero premio. Los ganadores generan historia en RFW.
          </p>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {[
              { label: 'Ganadores', value: '4,238', icon: 'ri-trophy-line' },
              { label: 'Hoy', value: '12', icon: 'ri-calendar-line' },
              { label: 'Puntos', value: '847K', icon: 'ri-flashlight-line' },
              { label: 'Leyendas', value: winnerGlobalStats.leyendasCount.toString(), icon: 'ri-vip-crown-line' },
              { label: 'Campeones', value: winnerGlobalStats.campeonesCount.toString(), icon: 'ri-shield-star-line' },
              { label: 'Activas', value: winnerGlobalStats.activeCompetitions.toString(), icon: 'ri-sword-line' },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center p-3 rounded-lg bg-white/70 backdrop-blur-sm border border-amber-100">
                <i className={`${stat.icon} text-amber-500 text-lg mb-1`}></i>
                <span className="text-lg font-bold text-foreground-950">{stat.value}</span>
                <span className="text-[10px] text-foreground-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* ─── TYPE FILTERS ─── */}
        <div className="py-6 border-b border-background-200/70">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none flex-wrap">
            {typeTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveType(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeType === tab.id
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── TIME FILTERS + WALL TABS ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4">
          <div className="flex items-center gap-1">
            {Object.entries(winnerTimeFilterLabels).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setActiveTime(id as WinnerTimeFilter)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTime === id
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${winnerTimeFilterIcons[id as WinnerTimeFilter]} text-[10px]`}></i>
                {label}
              </button>
            ))}
          </div>
          <span className="text-xs text-foreground-500">{filtered.length} ganadores encontrados</span>
        </div>

        {/* ─── WINNERS GRID ─── */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-12">
            {filtered.map((winner) => (
              <WinnerGridCard key={winner.id} winner={winner} onClick={() => setSelectedWinner(winner)} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 mb-12">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-trophy-line text-2xl text-amber-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">No hay ganadores con los filtros actuales</p>
            <button
              onClick={() => { setActiveType('todos'); setActiveTime('historico'); }}
              className="mt-3 text-xs text-amber-600 hover:text-amber-700 font-medium"
            >
              Limpiar filtros
            </button>
          </div>
        )}

        {/* ─── MURO DE GANADORES ─── */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <i className="ri-building-line text-amber-600 text-sm"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground-950">Muro de Ganadores</h3>
              <p className="text-xs text-foreground-500">Ganadores visibles por ubicación en la plataforma</p>
            </div>
          </div>

          {/* Wall Tabs */}
          <div className="flex items-center gap-1 mb-5">
            {(Object.entries(wallTypeLabels) as [WallType, string][]).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setActiveWall(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeWall === id
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${wallTypeIcons[id]} text-[10px]`}></i>
                {label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeWall === id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{wallWinners.length}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {wallWinners.slice(0, 12).map((winner) => (
              <WinnerGridCard key={winner.id} winner={winner} onClick={() => setSelectedWinner(winner)} />
            ))}
          </div>
        </section>

        {/* ─── SISTEMA DE INSIGNIAS ─── */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <i className="ri-award-line text-amber-600 text-sm"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground-950">Insignias de Ganador</h3>
              <p className="text-xs text-foreground-500">Cada victoria desbloquea reconocimiento permanente</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {winnerBadgeConfigs.map((badge) => (
              <BadgeCard key={badge.id} badge={badge} />
            ))}
          </div>
        </section>

        {/* ─── SCORE INFO ─── */}
        <section className="mb-12 p-6 rounded-2xl bg-background-100 border border-background-200/70">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
              <i className="ri-information-line text-white text-sm"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950">¿Cómo Funciona el Sistema de Ganadores?</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-user-add-line text-amber-600"></i>
                <span className="text-sm font-semibold text-foreground-900">1. Participar</span>
              </div>
              <p className="text-xs text-foreground-600">Participar en cualquier concurso, trivia, encuesta, evento o competencia ya te da puntos. No necesitás ganar para sumar.</p>
            </div>
            <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-trophy-line text-amber-600"></i>
                <span className="text-sm font-semibold text-foreground-900">2. Ganar</span>
              </div>
              <p className="text-xs text-foreground-600">Ganar te da puntos extra, insignias y reconocimiento social. Tu nombre aparece en el Muro de Ganadores y en la landing.</p>
            </div>
            <div className="p-4 rounded-lg bg-background-50 border border-background-200/70">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-vip-crown-line text-amber-600"></i>
                <span className="text-sm font-semibold text-foreground-900">3. Prestigio</span>
              </div>
              <p className="text-xs text-foreground-600">Las insignias son permanentes. Ser Leyenda RFW es el máximo honor. Los ganadores acumulan prestigio que atrae más seguidores y oportunidades.</p>
            </div>
          </div>
        </section>

        {/* ─── REGLA ─── */}
        <section className="mb-16 text-center py-10 px-6 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="text-4xl mb-3">👑</div>
          <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            Los Ganadores Generan Prestigio. No Solamente Premios.
          </h3>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm">
            En RFW creemos que el verdadero valor de ganar no está en el premio físico — está en el reconocimiento
            de la comunidad, las insignias que duran para siempre y el prestigio que abre puertas.
          </p>
        </section>
      </div>

      {/* ─── MODAL ─── */}
      {selectedWinner && (
        <WinnerDetailModal winner={selectedWinner} onClose={() => setSelectedWinner(null)} />
      )}
    </div>
  );
}